import type { MethodLocale } from '../types'

export const enMethod: MethodLocale = {
  hero: {
    tag: 'Method · Foundations',
    title: 'Why ontology actually moves the needle',
    subtitle: 'A long-form read · the companion to the demo and the lab',
    leadParagraph:
      'The demo shows it. The lab lets you play with it. This page explains why it works — what ontology is, why LLMs alone fall short in production, what ontology adds, and how an enterprise actually puts it in place.',
  },
  whatIs: {
    sectionTag: '§ A · What it is',
    title: 'Ontology is the ',
    titleSpan: 'digital mirror of your business',
    leadLine: 'A 30-second working definition',
    leadBody:
      'Ontology is the digital mirror of the real-world business — objects, relations, constraints, actions — captured so an AI agent can reason and act over it at runtime, instead of memorising hard-coded workflows at design time.',
    comparisonTitle: 'Ontology vs Knowledge Graph',
    comparisonHeaderKg: 'Traditional Knowledge Graph',
    comparisonHeaderOnto: 'Ontology',
    comparisonRows: [
      { label: 'Knows "what is"', kg: 'Yes', onto: 'Yes' },
      { label: 'Knows "how to act"', kg: '—', onto: 'Yes' },
      { label: 'Updates as the business evolves', kg: 'Hard', onto: 'Native' },
      { label: 'Triggers business operations', kg: '—', onto: 'Yes — via Action' },
      { label: 'Reasoning style', kg: 'Static query', onto: 'Dynamic (ontology + LLM)' },
      { label: 'Modelling primitives', kg: 'Entity · relation · property', onto: 'Property graph + Action + Logic' },
    ],
    layersTitle: 'Three layers of an ontology',
    layersHint: 'An enterprise ontology is not a single static layer. Three behaviours sit in the same model.',
    layers: [
      {
        tag: 'Declarative',
        name: 'What the business knows',
        description: 'Entities, relations, events, attributes. The structured representation of static enterprise knowledge.',
      },
      {
        tag: 'Procedural',
        name: 'What the business does',
        description: 'Queries, workflows, autonomous reasoning. Business logic and operating procedures captured as something the system can execute.',
      },
      {
        tag: 'Dynamic',
        name: 'What the business is doing right now',
        description: 'Live state, context, recent events. The ontology evolves with the business, no retraining required.',
      },
    ],
    exampleTag: 'A concrete example',
    exampleTitle: 'An airline asks "why was this flight late?"',
    exampleBody:
      'A traditional pipeline pulls maintenance, weather, and check-in records into a spreadsheet and an analyst diffs them. An ontology-backed agent already understands "flight", "aircraft", "weather", "ground crew" and their relations — ask "find delays caused by inbound-aircraft late arrival, with maintenance and weather", and the causal chain renders instantly, with "dispatch a standby aircraft" as a one-click action.',
  },
  whyMatters: {
    sectionTag: '§ B · Why it matters',
    title: 'Why LLMs ',
    titleSpan: 'need a foundation',
    lead: 'Generic LLMs are probability machines. Six things break the moment you point them at an enterprise stack.',
    challengesTitle: 'The six challenges of dropping a generic LLM into production',
    challenges: [
      {
        number: '01',
        title: 'Hallucination compounds',
        body: '30-step reasoning chains turn a 5% per-step error into an 80% wrong outcome. Industrial workflows can\'t absorb that.',
      },
      {
        number: '02',
        title: 'Unstable output',
        body: 'Same question, different answer each run. Audit, compliance and SOPs all need deterministic output.',
      },
      {
        number: '03',
        title: 'Weak self-planning',
        body: 'Long tasks need a framework. Without one, the agent wanders or stops prematurely.',
      },
      {
        number: '04',
        title: 'No proactive response',
        body: 'Pure Q&A. Can\'t fire on data changes, can\'t watch drift, can\'t trigger workflows.',
      },
      {
        number: '05',
        title: 'Hard data integration',
        body: 'ERP / MES / CRM silos. Same word means different things across systems. The LLM can\'t bridge that on its own.',
      },
      {
        number: '06',
        title: 'Slow cold start',
        body: 'From "we need this agent" to "agent in production" takes weeks to months of bespoke engineering.',
      },
    ],
    valuesTitle: 'What ontology adds',
    valuesHint: 'Three values — each addressing a specific failure mode above.',
    values: [
      {
        label: 'Semantic unification',
        body: 'Kill the "is it Apple Inc. or the apple fruit?" gap once. Departments, systems, and the LLM all share one definition. No more "ERP says X, MES says Y".',
      },
      {
        label: 'Trusted reasoning',
        body: 'Every step traceable back to a rule or a data source. Audit-ready by construction, not by retrofit.',
      },
      {
        label: 'Behavioural control',
        body: 'Hard business constraints become hard agent constraints. Not "please don\'t hallucinate" — "this rule is the rule, the agent literally cannot violate it".',
      },
    ],
    bridgeTag: 'The shorthand',
    bridgeTitle: 'Ontology + LLM = the agent that ships',
    bridgeBody:
      'LLMs do the language and reasoning. Ontology gives them the world model. Neither half is sufficient alone. The L3 stage of the demo is literally this: same LLM as L1 and L2, but with an ontology underneath.',
  },
  howToUse: {
    sectionTag: '§ C · How to put it in place',
    title: 'What an enterprise actually ',
    titleSpan: 'builds',
    lead: 'Three capabilities have to come together. Skip one and the others are unstable.',
    capabilitiesTitle: 'Three required capabilities',
    capabilities: [
      {
        number: '01',
        title: 'Data + system integration',
        body: 'Pull every system into one semantic layer. Three sub-layers underneath: semantic (entity unification), enablement (action triggers), dynamic (rule constraints).',
      },
      {
        number: '02',
        title: 'Dynamic ontology management',
        body: 'Keep the model alive as the business evolves. Rules and constraints stay current without re-training the LLM. Domain experts can edit the model directly.',
      },
      {
        number: '03',
        title: 'Precision app generation',
        body: 'Applications are generated from the domain ontology, not from a prompt. Each application is a faithful business twin, not a clever chatbot.',
      },
    ],
    enginesTitle: 'Workflow + Agentic — the dual engine',
    enginesHint: 'One engine isn\'t enough. Deterministic work and exploratory work look totally different.',
    engines: [
      {
        tag: 'Workflow engine',
        name: 'For audit-grade, deterministic flows',
        body: 'Fixed pipelines: data ingestion, monthly close, regulatory reports. The path is known; consistency and traceability are what matters.',
      },
      {
        tag: 'Agentic engine',
        name: 'For exploratory, plan-on-the-fly tasks',
        body: 'Investigations, what-ifs, multi-step diagnosis. The agent plans from the ontology at runtime instead of replaying a recorded script.',
      },
    ],
    enginesLinkLabel: 'See both engines in action in the Lab →',
    contextTitle: 'Context engineering — what the LLM actually sees',
    contextHint: 'The ontology isn\'t just data. It assembles four kinds of context, on demand, for each task.',
    contextRows: [
      { asset: 'Data Description', includes: 'Entities, relations, events, attributes — the structural model' },
      { asset: 'Data Operations', includes: 'Allowed agent actions (create / read / update / delete) — what the agent is permitted to do' },
      { asset: 'Logic & Process', includes: 'Workflows, business rules, planning logic — the procedural side' },
      { asset: 'Pages Description', includes: 'List views, charts, detail pages — the application surface that gets generated' },
    ],
  },
  adoption: {
    sectionTag: '§ D · What adoption looks like',
    title: 'Adoption ',
    titleSpan: 'in practice',
    lead: 'No silver bullet. But the path is well-trodden and the timeline is predictable.',
    phasesTitle: 'Typical timelines',
    phases: [
      { scope: 'Business-validation scope', duration: '1-2 weeks' },
      { scope: 'General scope', duration: 'Under 1 month' },
      { scope: 'High-complexity scope', duration: '1-6 months' },
    ],
    stepsTitle: 'Six steps',
    steps: [
      'Business needs confirmation',
      'Data ingestion + exploration',
      'Ontology definition + knowledge mapping',
      'Data alignment + governance',
      'Build + validation',
      'Pilot + iteration into production',
    ],
    deploymentTitle: 'Deployment modes',
    deploymentHint: 'Pick by data sensitivity, not by buzzword.',
    deploymentRows: [
      { mode: 'On-prem', fit: 'Finance · government · healthcare · regulated manufacturing' },
      { mode: 'Cloud', fit: 'SMB · low-sensitivity workloads · fast iteration' },
      { mode: 'Hybrid', fit: 'Mixed-sensitivity portfolio across business units' },
    ],
  },
  beyondBasics: {
    sectionTag: '§ F · Beyond the basics',
    title: 'Beyond the basics — ',
    titleSpan: 'perspectives from the field',
    lead: 'Three sharper observations from real customer engagements. These build on §A-§E; they do not replace them.',
    threeLayersTitle: 'The three layers that actually ship: Ontology · Harness · Agent',
    threeLayersHint:
      'When model vendors (OpenAI DeployCo, Anthropic enterprise Claude) compete on "delivery", the gap that remains is the semantic layer. Each company has to build that part itself.',
    threeLayers: [
      {
        tag: 'Ontology',
        name: 'Business semantic layer',
        body: 'Objects, relations, rules, processes, permissions — pulled out of docs, systems and tribal knowledge into one structured world. The company\'s job, not the model vendor\'s.',
      },
      {
        tag: 'Harness',
        name: 'Execution governance',
        body: 'Not "smarter", but "steadier": boundaries, state, validation, rollback, audit trail. Model vendors are beginning to touch this layer; it still mostly sits with the customer.',
      },
      {
        tag: 'Agent',
        name: 'Task execution',
        body: 'The layer everyone competes on. But: without the two layers below, agents stay limited to isolated actions.',
      },
    ],
    symptomsTitle: 'How "no semantic layer" actually shows up',
    symptomsHint: 'When an enterprise has model + agent + tools but no ontology, the symptoms are predictable:',
    symptoms: [
      'Can answer the question, but the answer doesn\'t enter the business process',
      'Can call tools, but doesn\'t know when to call, when to stop',
      'Can generate content, but voice is impossible to keep consistent',
      'Can handle local work, but cross-system gets shaky',
      'Can produce results, but results don\'t accumulate into organisational assets',
    ],
    contrastTitle: 'Looks like working vs really participating',
    contrastHint: 'Six axes to distinguish "AI that demos well" from "AI that ships":',
    contrastHeaderShallow: 'Looks like working',
    contrastHeaderDeep: 'Really participating',
    contrastRows: [
      { label: 'Answer', shallow: 'Fluent', deep: 'Accurate' },
      { label: 'Decisions', shallow: 'Stays out', deep: 'Provides judgement path + data evidence' },
      { label: 'Business access', shallow: 'Surface API', deep: 'Inside the business process' },
      { label: 'Cross-system', shallow: 'Gets shaky', deep: 'Naturally bridged via shared semantic layer' },
      { label: 'Sediment', shallow: 'One-off response', deep: 'Continuously turns into organisational asset' },
      { label: 'Trust source', shallow: 'Model params', deep: '"It actually understands my business"' },
    ],
    playbookCta: {
      tag: 'Operational guide',
      title: 'Read the Playbook',
      body: 'Practical patterns for shipping ontology + agent — where to start, how to model, which engine to pick, how to control behaviour, how to keep audit trails, how to handle cold start. 11 sections, all from real projects.',
      cta: 'Open the Playbook →',
    },
  },
  next: {
    sectionTag: '§ E · Next',
    title: 'Now ',
    titleSpan: 'see it · play with it · find your sector',
    lead: 'You read the method. The next three pages turn it into something you can poke at.',
    cards: [
      {
        tag: 'See',
        title: 'The Demo',
        body: 'A narrated walkthrough of L1 → L2 → L3 on the petroleum procurement scenario. Five-minute auto demo with English narration.',
        cta: 'Open the Demo →',
        target: 'demo',
      },
      {
        tag: 'Play',
        title: 'The Lab',
        body: 'Ten interactive playgrounds — one per paradigm. Fire alarms, drop documents, drag thresholds, watch the chain rebuild itself.',
        cta: 'Open the Lab →',
        target: 'lab',
      },
      {
        tag: 'Your sector',
        title: 'Industries',
        body: '11 industries × 3 paradigms each, re-skinned with industry-specific data. Finance · healthcare · legal · automotive · logistics · government and more.',
        cta: 'Browse Industries →',
        target: 'industries',
      },
    ],
  },
}
